// The Follow-Through Plan quiz: a close copy of Purpose's web funnel (join.purpose.app,
// walked 31 Oct 2026), with Purpose/Mark Manson swapped for LifeHack AI Coach/Leon Ho.
// Order, count, wording and answers match the original on purpose (stepcase/timeblock#1273):
// if this version fails, the cause is the audience or product, not a change we made.

export const FOLLOW_THROUGH_PLAN_ID = 'follow-through-plan';

export type QuestionIcon =
  | 'heart'
  | 'list'
  | 'compass'
  | 'route'
  | 'person'
  | 'sparkle'
  | 'circle'
  | 'clock'
  | 'cloud'
  | 'zap'
  | 'message'
  | 'pen'
  | 'moon'
  | 'smartphone'
  | 'newspaper'
  | 'tv'
  | 'more';

export interface QuizOption {
  value: string;
  label: string;
  icon?: QuestionIcon;
}

export interface QuizQuestion {
  id: QuestionId;
  title: string;
  subtitle?: string;
  options: QuizOption[];
  /** Pick several, then Continue. Every other question moves on with one tap. */
  multiSelect?: boolean;
}

export const QUESTION_IDS = [
  'goal',
  'identity',
  'age',
  'mentalState',
  'obstacle',
  'screenTime',
  'processing',
  'appHistory',
  'dailyTime',
  'successSignal',
  'setbacks',
  'mostTrue',
  'routineChange',
  'accountability',
  'urgency',
] as const;

export type QuestionId = (typeof QUESTION_IDS)[number];

export const GOAL_VALUES = ['stress', 'habits', 'direction', 'transition'] as const;
export type GoalValue = (typeof GOAL_VALUES)[number];

export const OBSTACLE_VALUES = ['no-time', 'motivation', 'one-size', 'accountability'] as const;
export type ObstacleValue = (typeof OBSTACLE_VALUES)[number];

export const QUESTIONS: QuizQuestion[] = [
  {
    id: 'goal',
    title: "What's the #1 thing you want to change in your life right now?",
    subtitle: 'Be honest, this shapes everything that follows',
    options: [
      { value: 'stress', label: 'Reduce stress & anxiety', icon: 'heart' },
      { value: 'habits', label: 'Build better daily habits', icon: 'list' },
      { value: 'direction', label: 'Find direction & purpose', icon: 'compass' },
      { value: 'transition', label: 'Dealing with a major life transition', icon: 'route' },
    ],
  },
  {
    id: 'identity',
    title: 'How should your coach personalize examples for you?',
    subtitle: 'Optional context for examples and coach tone',
    options: [
      { value: 'woman', label: 'Woman', icon: 'person' },
      { value: 'man', label: 'Man', icon: 'person' },
      { value: 'prefer-not', label: 'Prefer not to say', icon: 'circle' },
    ],
  },
  {
    id: 'age',
    title: "What's your age range?",
    subtitle: 'This helps tune examples and plan pacing',
    options: ['18-24', '25-29', '30-34', '35-44', '45-54', '55+'].map((range) => ({
      value: range,
      label: range,
    })),
  },
  {
    id: 'mentalState',
    title: 'How would you describe your current mental state?',
    options: [
      { value: 'overwhelmed', label: 'Overwhelmed, too much going on', icon: 'cloud' },
      { value: 'stuck', label: "Stuck, know what to do but can't start", icon: 'circle' },
      { value: 'anxious', label: 'Anxious, constantly worried about things', icon: 'heart' },
      { value: 'okay', label: 'Okay, but know I could be better', icon: 'sparkle' },
    ],
  },
  {
    id: 'obstacle',
    title: "What's your biggest obstacle to self-improvement?",
    options: [
      { value: 'no-time', label: 'No time, life is too busy', icon: 'clock' },
      { value: 'motivation', label: "Motivation, I start but can't sustain", icon: 'zap' },
      { value: 'one-size', label: "One-size-fits-all advice doesn't work for me", icon: 'list' },
      { value: 'accountability', label: 'No one to hold me accountable', icon: 'person' },
    ],
  },
  {
    id: 'screenTime',
    title: "Are there any screen-time habits you'd like to change?",
    subtitle: 'Select all that apply',
    multiSelect: true,
    options: [
      { value: 'social', label: 'Scrolling social media', icon: 'smartphone' },
      { value: 'news', label: 'News and doomscrolling', icon: 'newspaper' },
      { value: 'video', label: 'Videos or streaming', icon: 'tv' },
      { value: 'other', label: 'Other', icon: 'more' },
    ],
  },
  {
    id: 'processing',
    title: 'How do you prefer to process your thoughts?',
    options: [
      { value: 'talking', label: 'Talking it through with someone', icon: 'message' },
      { value: 'writing', label: 'Writing or journaling', icon: 'pen' },
      { value: 'quiet', label: 'Quiet reflection alone', icon: 'moon' },
      { value: 'action', label: 'Taking action and learning by doing', icon: 'zap' },
    ],
  },
  {
    id: 'appHistory',
    title: "What's your relationship with self-improvement apps?",
    options: [
      { value: 'none-stuck', label: 'Tried many, none stuck' },
      { value: 'skeptical', label: 'Skeptical, they feel too robotic' },
      { value: 'overwhelmed', label: 'Overwhelmed by too many options' },
      { value: 'hopeful', label: 'Hopeful, looking for the right one' },
    ],
  },
  {
    id: 'dailyTime',
    title: 'How much time can you realistically commit daily?',
    options: [
      { value: '5', label: 'Just 5 minutes', icon: 'clock' },
      { value: '10', label: 'About 10 minutes', icon: 'clock' },
      { value: '15-20', label: '15-20 minutes', icon: 'clock' },
      { value: 'varies', label: 'It varies day to day', icon: 'route' },
    ],
  },
  {
    id: 'successSignal',
    title: 'What would make you feel like your coach is working?',
    subtitle: "How will you know you're making progress?",
    options: [
      { value: 'calmer', label: 'Feeling calmer and more in control', icon: 'heart' },
      { value: 'habits', label: 'Actually sticking to new habits', icon: 'list' },
      { value: 'clarity', label: 'Having clarity on what I want', icon: 'compass' },
      { value: 'energy', label: 'More energy and motivation', icon: 'zap' },
    ],
  },
  {
    id: 'setbacks',
    title: 'How do you usually handle setbacks or bad days?',
    options: [
      { value: 'push', label: 'Push through and keep going' },
      { value: 'talk', label: 'Talk to someone I trust' },
      { value: 'reset', label: 'Take a break and reset' },
      { value: 'spiral', label: 'Overthink and spiral a bit' },
    ],
  },
  {
    id: 'mostTrue',
    title: 'Which of these feels most true for you right now?',
    subtitle: "There's no wrong answer",
    options: [
      { value: 'wrong-things', label: 'I need to stop caring about the wrong things' },
      { value: 'structure', label: 'I need more structure in my life' },
      { value: 'understand', label: 'I need to understand myself better' },
      { value: 'start-doing', label: 'I need to stop overthinking and start doing' },
    ],
  },
  {
    id: 'routineChange',
    title: 'If you could change one thing about your daily routine?',
    options: [
      { value: 'morning', label: 'How I start my morning' },
      { value: 'free-time', label: 'How I spend my free time' },
      { value: 'energy', label: 'How I manage my energy' },
      { value: 'wind-down', label: 'How I wind down at night' },
    ],
  },
  {
    id: 'accountability',
    title: 'How important is having someone hold you accountable?',
    options: [
      { value: 'critical', label: 'Critical, I need external structure' },
      { value: 'helpful', label: 'Helpful but not essential' },
      { value: 'self', label: 'I prefer self-motivation' },
      { value: 'never-tried', label: 'Never really tried it' },
    ],
  },
  {
    id: 'urgency',
    title: 'How soon do you want to start seeing change?',
    options: [
      { value: 'this-week', label: "This week, I'm ready now" },
      { value: 'month', label: 'Within a month' },
      { value: 'long-haul', label: "I'm in it for the long haul" },
      { value: 'not-sure', label: "Not sure, that's part of the problem" },
    ],
  },
];

/** Question 16 is the first-name field. */
export const NAME_QUESTION = {
  title: "Last one: what's your name?",
  subtitle: 'So your coach can address you personally',
  placeholder: 'Your first name',
  cta: 'Build My Personal Plan',
} as const;

export const TOTAL_QUESTIONS = QUESTIONS.length + 1;

export type InsightScreenNumber = 1 | 2 | 3;

/** The explaining screens sit after these questions. */
export const INSIGHT_AFTER: Record<InsightScreenNumber, QuestionId> = {
  1: 'screenTime',
  2: 'setbacks',
  3: 'accountability',
};

export type Answers = Partial<Record<QuestionId, string | string[]>>;

export function answerLabel(questionId: QuestionId, value: string | string[] | undefined): string {
  const question = QUESTIONS.find((q) => q.id === questionId);
  if (!question || value === undefined) return '';
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((v) => question.options.find((option) => option.value === v)?.label ?? v)
    .join(', ');
}
